import { useQuery } from '@tanstack/react-query';
import { Box, Grid, Typography } from '@mui/material';
import ContactsIcon from '@mui/icons-material/ContactsOutlined';
import TodayIcon from '@mui/icons-material/TodayOutlined';
import FacebookIcon from '@mui/icons-material/Facebook';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/PhoneOutlined';
import SchoolIcon from '@mui/icons-material/SchoolOutlined';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoffOutlined';
import FlightLandIcon from '@mui/icons-material/FlightLandOutlined';
import PersonOffIcon from '@mui/icons-material/PersonOffOutlined';
import AssignmentIndIcon from '@mui/icons-material/AssignmentIndOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import {
  getConversionMetrics,
  getDashboardStats,
  getDocumentReviewCounts,
  getLeadChart,
  getPendingDocumentsCount,
  getRegisteredStudentsCount,
} from '../../api/dashboard';
import { useAuth } from '../../context/authContext';
import { useBranchFilter } from '../../context/branchContext';
import { useNewLeadStatusId } from '../../hooks/useNewLeadStatusId';
import { KpiCard, KpiGrid } from './KpiCard';
import { LeadTrendChart } from './LeadTrendChart';
import { ConversionCard } from './ConversionCard';
import { CounselorLeaderboardCard } from './CounselorLeaderboardCard';
import { FollowUpOverviewCard } from './FollowUpOverviewCard';
import { LeadSourcesCard } from './LeadSourcesCard';
import { PipelineOverviewCard } from './PipelineOverviewCard';
import { RecentLeadsCard } from './RecentLeadsCard';
import { LatestAdmissionsCard } from './LatestAdmissionsCard';
import { RecentPaymentsCard } from './RecentPaymentsCard';
import { ReadyToFlyCard } from './ReadyToFlyCard';
import { RecentAssignmentsCard } from './RecentAssignmentsCard';
import { PendingApprovalsCard } from './PendingApprovalsCard';
import { InactiveCounselorLeadsCard } from './InactiveCounselorLeadsCard';
import { PendingLeadsByCounselorCard } from './PendingLeadsByCounselorCard';
import { CounselorDashboardView } from './CounselorDashboardView';
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined';
import { DashboardLoadError } from './DashboardLoadError';

/** Only a real day-over-day figure counts as a trend — everything else here is a lifetime/snapshot total with no comparable prior value, so it gets no badge rather than an invented one. */
const useTodaysLeadsTrend = (enabled: boolean, branchId: number | undefined) => {
  const chartQuery = useQuery({
    queryKey: ['lead-chart', 'daily', branchId],
    queryFn: () => getLeadChart('daily', branchId),
    enabled,
  });
  const buckets = chartQuery.data ?? [];
  if (buckets.length < 2) return null;
  const today = buckets[buckets.length - 1].count;
  const yesterday = buckets[buckets.length - 2].count;
  if (yesterday === 0) return today > 0 ? 100 : null;
  return Math.round(((today - yesterday) / yesterday) * 100);
};

export const DashboardPage = () => {
  const { user, hasPermission } = useAuth();
  // Phase 76 branch selector — inert (always undefined) for anyone but
  // Super Admin/Lead Manager (see BranchProvider.tsx), so this is safe to
  // thread through unconditionally; the backend ignores it for everyone
  // else regardless.
  const { selectedBranchId } = useBranchFilter();
  // "Pending leads" KPI click-through — the real "New" status id, so
  // this matches exactly what the KPI counted (see the KPI's own
  // comment below) rather than the Leads list's broader `pending=true`.
  const newStatusId = useNewLeadStatusId();
  // Counselor Dashboard correction (spec §22) — a Counselor gets an
  // entirely separate, personal-activity-only layout. Every hook below
  // still runs unconditionally (rules-of-hooks), but each is gated so a
  // Counselor never actually fetches any company-wide data — see the
  // early return after them, once every hook has been called.
  const isCounselor = user?.role.name === 'Counselor';

  const canViewLeads = !isCounselor && hasPermission('leads', 'view');
  const canViewStudents = !isCounselor && hasPermission('students', 'view');
  const canViewAcademic = !isCounselor && hasPermission('academic', 'view');
  const canViewPayments = !isCounselor && hasPermission('payments', 'view');
  const canViewReadyToFly = !isCounselor && hasPermission('ready_to_fly', 'view');
  // Lead Manager (and anyone else who can distribute leads — Super Admin,
  // Branch Manager) gets the central-pool health metrics; a Counselor
  // with only leads:view/create never sees these.
  const canManageAssignments = !isCounselor && hasPermission('leads', 'assign');
  const canApproveLeads = !isCounselor && hasPermission('leads', 'approve');
  // Admission Officer Dashboard correction: Lead Volume/Conversion/Counselor
  // Leaderboard are Lead-pipeline analytics — gate them on leads:view (the
  // same permission the sibling Recent Leads/Follow-up/Lead Sources cards
  // already use) so a role with no Lead visibility at all never fetches or
  // renders them, matching the backend's own new leads:view gate on these
  // same three endpoints.
  const canViewLeadAnalytics = canViewLeads;
  // Pending Documents needs the capability to actually act on a pending
  // document (verify/reject it) — narrower than just students:view.
  const canViewPendingDocuments = !isCounselor && hasPermission('documents', 'verify');

  const statsQuery = useQuery({
    queryKey: ['dashboard-stats', selectedBranchId],
    queryFn: () => getDashboardStats(selectedBranchId),
    enabled: !isCounselor,
  });
  const conversionQuery = useQuery({
    queryKey: ['conversion-metrics', selectedBranchId],
    queryFn: () => getConversionMetrics(selectedBranchId),
    enabled: canViewLeadAnalytics,
  });
  const pendingDocumentsQuery = useQuery({
    queryKey: ['dashboard-pending-documents', selectedBranchId],
    queryFn: () => getPendingDocumentsCount(selectedBranchId),
    enabled: canViewPendingDocuments,
  });
  // Admission Officer module: informational approved/rejected tallies,
  // same gate as Pending Documents above (documents:verify) since both
  // are about the same document-review workflow.
  const documentReviewCountsQuery = useQuery({
    queryKey: ['dashboard-document-review-counts', selectedBranchId],
    queryFn: () => getDocumentReviewCounts(selectedBranchId),
    enabled: canViewPendingDocuments,
  });
  // Admission Officer automatic-visibility (§1.3): live count of students
  // whose registration is complete and registration payment is approved —
  // the same trigger used by the Students list's own
  // registrationPaymentApproved filter, so this card's click-through can
  // never show a different number than what it promised.
  const registeredStudentsCountQuery = useQuery({
    queryKey: ['dashboard-registered-students-count', selectedBranchId],
    queryFn: () => getRegisteredStudentsCount(selectedBranchId),
    enabled: canViewPendingDocuments,
  });
  const todaysTrend = useTodaysLeadsTrend(canViewLeads, selectedBranchId);

  const s = statsQuery.data;

  if (isCounselor) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
          My Dashboard
        </Typography>
        <CounselorDashboardView />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
        Dashboard
      </Typography>
      {statsQuery.isError && <DashboardLoadError error={statsQuery.error} onRetry={() => void statsQuery.refetch()} />}

      <KpiGrid>
        {canViewLeads && (
          <>
            <Box sx={{ minWidth: 0 }}>
              <KpiCard compact label="Total leads" value={s?.totalLeads ?? '—'} icon={ContactsIcon} color="#5B2DA3" to="/leads" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <KpiCard compact
                label="Today's leads"
                value={s?.todaysLeads ?? '—'}
                icon={TodayIcon}
                color="#2F6FED"
                to="/leads"
                trendPercent={todaysTrend}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <KpiCard compact label="Facebook leads" value={s?.facebookLeads ?? '—'} icon={FacebookIcon} color="#1877F2" to="/leads" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <KpiCard compact label="WhatsApp leads" value={s?.whatsappLeads ?? '—'} icon={WhatsAppIcon} color="#25D366" to="/leads" />
            </Box>
          </>
        )}
        {canViewLeads && (
          <Box sx={{ minWidth: 0 }}>
            <KpiCard compact label="Telephone leads" value={s?.telephoneLeads ?? '—'} icon={PhoneIcon} color="#F5A623" to="/leads" />
          </Box>
        )}
        {canManageAssignments && (
          <>
            <Box sx={{ minWidth: 0 }}>
              <KpiCard compact
                label="Unassigned leads"
                value={s?.unassignedLeads ?? '—'}
                icon={PersonOffIcon}
                color="#D92D20"
                to="/lead-distribution"
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <KpiCard compact
                label="Assigned leads"
                value={s?.assignedLeads ?? '—'}
                icon={AssignmentIndIcon}
                color="#12B76A"
                to="/leads"
              />
            </Box>
          </>
        )}
        {canApproveLeads && (
          <Box sx={{ minWidth: 0 }}>
            <KpiCard compact
              label="Pending approval"
              value={s?.pendingApprovalLeads ?? '—'}
              icon={FactCheckIcon}
              color="#F5A623"
              to="/lead-approvals"
            />
          </Box>
        )}
        {canViewLeads && (
          <Box sx={{ minWidth: 0 }}>
            {/* Super Admin Dashboard "Pending Leads" card — derived from
                the exact same shared dataset (PENDING_LEAD_BY_COUNSELOR_WHERE,
                see dashboard.service.ts's getPendingLeadsDataset) that
                "Pending Leads by Counselor" below is built from, so this
                number always exactly equals the sum of that widget's own
                rows + Unassigned — never a second, independently-
                calculated count. Distinct from "Pending approval" above
                (that's Lead.approvalStatus === PENDING_APPROVAL, the
                Quick-Lead review queue — see PENDING_LEAD_WHERE's own
                comment in leads.service.ts for how the two differ). The
                click-through uses the same real "New" status id the
                breakdown's own rows link to, not the Leads list's
                broader `pending=true`, so what's shown after clicking
                always matches what this card counted. */}
            <KpiCard compact
              label="Pending leads"
              value={s?.pendingLeads ?? '—'}
              icon={PendingActionsOutlinedIcon}
              color="#7C3AED"
              to={newStatusId ? `/leads?leadStatusId=${newStatusId}` : '/leads?pending=true'}
            />
          </Box>
        )}
        {canViewStudents && (
          <Box sx={{ minWidth: 0 }}>
            <KpiCard compact label="Registered" value={s?.registered ?? '—'} icon={SchoolIcon} color="#12B76A" to="/students" />
          </Box>
        )}
        {canViewAcademic && (
          <Box sx={{ minWidth: 0 }}>
            <KpiCard compact
              label="Admissions"
              value={s?.admissions ?? '—'}
              icon={AssignmentTurnedInIcon}
              color="#0BA5EC"
              to="/admissions"
            />
          </Box>
        )}
        {canViewPayments && (
          <Box sx={{ minWidth: 0 }}>
            <KpiCard compact label="Payments approved" value={s?.payments ?? '—'} icon={PaymentsIcon} color="#12B76A" to="/payments" />
          </Box>
        )}
        {canViewReadyToFly && (
          <>
            <Box sx={{ minWidth: 0 }}>
              <KpiCard compact
                label="Ready to fly"
                value={s?.readyToFly ?? '—'}
                icon={FlightTakeoffIcon}
                color="#F5A623"
                to="/ready-to-fly"
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <KpiCard compact label="Flown" value={s?.flown ?? '—'} icon={FlightLandIcon} color="#6B9BFA" to="/ready-to-fly" />
            </Box>
          </>
        )}
        {canViewPendingDocuments && (
          <Box sx={{ minWidth: 0 }}>
            <KpiCard compact
              label="Registered students"
              value={registeredStudentsCountQuery.data ?? '—'}
              icon={PeopleAltOutlinedIcon}
              color="#12B76A"
              to="/students?registrationPaymentApproved=true"
            />
          </Box>
        )}
        {canViewPendingDocuments && (
          <Box sx={{ minWidth: 0 }}>
            <KpiCard compact
              label="Pending documents"
              value={pendingDocumentsQuery.data ?? '—'}
              icon={DescriptionOutlinedIcon}
              color="#D92D20"
              to="/students?documents=pending"
            />
          </Box>
        )}
        {canViewPendingDocuments && (
          <Box sx={{ minWidth: 0 }}>
            <KpiCard compact
              label="Approved documents"
              value={documentReviewCountsQuery.data?.approved ?? '—'}
              icon={CheckCircleOutlinedIcon}
              color="#12B76A"
            />
          </Box>
        )}
        {canViewPendingDocuments && (
          <Box sx={{ minWidth: 0 }}>
            <KpiCard compact
              label="Rejected documents"
              value={documentReviewCountsQuery.data?.rejected ?? '—'}
              icon={CancelOutlinedIcon}
              color="#D92D20"
            />
          </Box>
        )}
      </KpiGrid>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        {canViewLeadAnalytics && (
          <Grid item xs={12} md={8}>
            <LeadTrendChart />
          </Grid>
        )}
        {canViewLeadAnalytics && (
          <Grid item xs={12} md={4}>
            <ConversionCard data={conversionQuery.data} />
          </Grid>
        )}

        {canViewLeads && (
          <Grid item xs={12} md={6}>
            <RecentLeadsCard />
          </Grid>
        )}
        {canViewAcademic && (
          <Grid item xs={12} md={6}>
            <LatestAdmissionsCard />
          </Grid>
        )}

        {canViewPayments && (
          <Grid item xs={12} md={6}>
            <RecentPaymentsCard />
          </Grid>
        )}
        {canViewReadyToFly && (
          <Grid item xs={12} md={6}>
            <ReadyToFlyCard />
          </Grid>
        )}

        {canViewLeadAnalytics && (
          <Grid item xs={12} md={4}>
            <CounselorLeaderboardCard />
          </Grid>
        )}
        {canViewLeads && (
          <Grid item xs={12} md={4}>
            <FollowUpOverviewCard />
          </Grid>
        )}
        {canViewLeads && (
          <Grid item xs={12} md={4}>
            <LeadSourcesCard stats={s} />
          </Grid>
        )}
        <Grid item xs={12} md={4}>
          <PipelineOverviewCard stats={s} />
        </Grid>
        {canManageAssignments && (
          <Grid item xs={12} md={4}>
            <RecentAssignmentsCard />
          </Grid>
        )}
        {canManageAssignments && (
          <Grid item xs={12} md={4}>
            <PendingLeadsByCounselorCard />
          </Grid>
        )}
        {canApproveLeads && (
          <Grid item xs={12} md={4}>
            <PendingApprovalsCard />
          </Grid>
        )}
        {canManageAssignments && (
          <Grid item xs={12} md={4}>
            <InactiveCounselorLeadsCard />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
